import { useState, useEffect } from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Loader2, AlertTriangle, User, Mail, Calendar, Clock } from 'lucide-react';
import { UserProfile, UserRole, ROLE_LABELS, getInviteStatus, formatInviteStatus } from '@/types/user';
import { projectAPI } from '@/utils/supabase/client';
import { formatDistanceToNow, format } from 'date-fns';
import { RoleBadge } from './RoleBadge';
import { ProjectAccessManager } from './ProjectAccessManager';

interface UserDetailSheetProps {
  user: UserProfile | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdate: () => void;
}

export function UserDetailSheet({ user, open, onOpenChange, onUpdate }: UserDetailSheetProps) {
  const [selectedRole, setSelectedRole] = useState<UserRole | ''>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);

  // Reset state when user changes
  useEffect(() => {
    if (user) {
      setSelectedRole(user.role);
      setError(null);
    }
  }, [user]);

  if (!user) return null;

  const isPending = user.role === 'pending';
  const inviteStatus = getInviteStatus(user);

  const handleSaveRole = async () => {
    if (!selectedRole || selectedRole === user.role) return;

    setSaving(true);
    setError(null);

    try {
      await projectAPI.updateUser(user.id, { role: selectedRole });
      onUpdate();
    } catch (err: any) {
      setError(err.message || 'Failed to update role');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    setSaving(true);
    setError(null);

    try {
      await projectAPI.deactivateUser(user.id);
      onUpdate();
      onOpenChange(false);
    } catch (err: any) {
      setError(err.message || 'Failed to deactivate user');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader className="px-6 pt-6 pb-4">
          <SheetTitle className="flex items-center gap-2 font-serif">
            <User className="h-6 w-6" />
            {user.display_name || 'User Details'}
          </SheetTitle>
          <SheetDescription className="mt-1">
            Manage user role and permissions
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-6 px-6 pb-6">
          {/* Pending User Warning */}
          {isPending && (
            <div className="p-4 rounded-lg bg-warning/15 border border-warning/20">
              <div className="flex gap-3">
                <AlertTriangle className="h-5 w-5 text-warning flex-shrink-0" />
                <div>
                  <p className="font-medium text-warning">
                    Role Assignment Required
                  </p>
                  <p className="text-sm text-warning/80 mt-1">
                    This user signed up and is waiting for you to assign their role.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* User Info */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">{user.email}</span>
            </div>

            <div className="flex items-center gap-3">
              <RoleBadge role={user.role} />
              <span className="text-sm text-muted-foreground">
                {formatInviteStatus(user)}
              </span>
            </div>

            {user.last_login_at && (
              <div className="flex items-center gap-3">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  Last login: {formatDistanceToNow(new Date(user.last_login_at), { addSuffix: true })}
                </span>
              </div>
            )}

            {user.created_at && (
              <div className="flex items-center gap-3">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  Joined: {format(new Date(user.created_at), 'MMM d, yyyy')}
                </span>
              </div>
            )}
          </div>

          <Separator className="my-6" />

          {/* Role Selection */}
          <div className="space-y-3">
            <Label>Role</Label>
            <Select
              value={selectedRole}
              onValueChange={(value: UserRole) => setSelectedRole(value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="super_admin">
                  {ROLE_LABELS.super_admin}
                </SelectItem>
                <SelectItem value="owner">
                  {ROLE_LABELS.owner}
                </SelectItem>
                <SelectItem value="pipeline_manager">
                  {ROLE_LABELS.pipeline_manager}
                </SelectItem>
                <SelectItem value="team_lead">
                  {ROLE_LABELS.team_lead}
                </SelectItem>
                <SelectItem value="team_member">
                  {ROLE_LABELS.team_member}
                </SelectItem>
                <SelectItem value="external_partner">
                  {ROLE_LABELS.external_partner}
                </SelectItem>
                <SelectItem value="intern">
                  {ROLE_LABELS.intern}
                </SelectItem>
              </SelectContent>
            </Select>

            <div className="text-xs text-muted-foreground space-y-1">
              {selectedRole === 'super_admin' && (
                <p>Full access to everything including user management.</p>
              )}
              {selectedRole === 'owner' && (
                <p>Can view all projects and financials, edit projects, but cannot delete or manage users.</p>
              )}
              {selectedRole === 'pipeline_manager' && (
                <p>Owns both prospect pipelines end to end — create, edit, advance, and disqualify prospects and journey records. Read-only on CRM clients. No portfolio, projects, financials, billing, or user management.</p>
              )}
              {selectedRole === 'team_lead' && (
                <p>Everything a Team Member has — assigned projects and their personal dashboard — plus full ownership of both prospect pipelines. No financials, billing, or user management. Configure project access below.</p>
              )}
              {selectedRole === 'team_member' && (
                <p>Can only see assigned projects. Configure project access below.</p>
              )}
              {selectedRole === 'external_partner' && (
                <p>Can only see granted projects with configurable visibility. Configure project access below.</p>
              )}
              {selectedRole === 'intern' && (
                <p>Scoped to the Intern OS section only. No access to portfolio, projects, or prospects.</p>
              )}
            </div>

            {selectedRole !== user.role && (
              <Button
                onClick={handleSaveRole}
                disabled={saving}
                className="w-full"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Role Change'
                )}
              </Button>
            )}
          </div>

          {/* Project Access (for team_lead, team_member and external_partner) */}
          {(selectedRole === 'team_lead' || selectedRole === 'team_member' || selectedRole === 'external_partner') && (
            <>
              <Separator className="my-6" />
              <ProjectAccessManager
                userId={user.id}
                userRole={selectedRole}
              />
            </>
          )}

          {/* Error Display */}
          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}

          <Separator className="my-6" />

          {/* Danger Zone */}
          <div className="space-y-3">
            <Label className="text-destructive">Danger Zone</Label>
            <Button
              variant="destructive"
              onClick={() => setShowDeactivateConfirm(true)}
              disabled={saving}
              className="w-full"
            >
              Deactivate User
            </Button>
            <p className="text-xs text-muted-foreground">
              Deactivating a user will revoke their access to the platform.
            </p>
          </div>
        </div>

        <AlertDialog open={showDeactivateConfirm} onOpenChange={setShowDeactivateConfirm}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Deactivate this user?</AlertDialogTitle>
              <AlertDialogDescription>
                {user.display_name || 'This user'} will lose access to the platform. You can
                re-invite them later if needed.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={handleDeactivate}
              >
                Deactivate
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </SheetContent>
    </Sheet>
  );
}
